import type { Ref } from 'vue'

import { resolveClient } from '@/shared/ports/resolveClient'
import {
  RaidApplyToBoardDocument,
  RaidCancelDocument,
  RaidCreateDocument,
  RaidDeleteDocument,
  RaidSetPublishedDocument,
  RaidUpdateDocument,
} from '../../lib/handWrittenGraphql'
import type { RaidCreateMutation } from '../../lib/handWrittenGraphql'
import type { RaidId, RaidRoom, RaidSummary } from '../../types'
import type {
  RaidGroup,
  RaidPlayer,
  RaidStage,
  RaidStageId,
  RaidBoardPayload,
} from '../../types'
import { useLoadRaids } from './loadRaids'

export interface RaidCreateClient {
  readonly mutate: (options: {
    readonly mutation: unknown
    readonly variables: Record<string, unknown>
  }) => Promise<{ readonly data?: RaidCreateMutation | null | undefined }>
}

// Local alias so scheduled creation and its focused behavioural test share
// the same narrow mutation contract without instantiating a second client.
const apolloClient: RaidCreateClient = resolveClient()

const MISSING_RAID_CREATE_ACKNOWLEDGEMENT =
  'Server did not acknowledge the created raid. Please try again.'

/**
 * Dispatch one scheduled-raid create intent and make the shared error ref
 * describe this request only. In particular, a successful retry must clear a
 * duplicate-name error left by the previous attempt; otherwise the modal
 * reports failure after the row has committed and invites another create.
 */
export async function requestRaidCreate(
  client: RaidCreateClient,
  error: Ref<string | null>,
  variables: Record<string, unknown>,
): Promise<RaidId | null> {
  error.value = null
  try {
    const response = await client.mutate({
      mutation: RaidCreateDocument,
      variables,
    })
    const newId = response.data?.raidCreate?.raidId
    if (newId === null || newId === undefined) {
      error.value = MISSING_RAID_CREATE_ACKNOWLEDGEMENT
      return null
    }
    error.value = null
    return String(newId)
  } catch (cause: unknown) {
    error.value = cause instanceof Error ? cause.message : String(cause)
    return null
  }
}

export interface RaidActionsCtx {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly error: Ref<string | null>
  readonly activeRaidId: Ref<RaidId | null>
  readonly raids: Ref<ReadonlyArray<RaidSummary>>
  readonly raidRooms: Ref<ReadonlyArray<RaidRoom>>
  readonly raidGroups: Ref<ReadonlyArray<RaidGroup>>
  readonly raidPlayers: Ref<ReadonlyArray<RaidPlayer>>
  readonly raidStages: Ref<ReadonlyArray<RaidStage>>
  readonly activeStageId: Ref<RaidStageId | null>
  readonly activeRaidBoard: Ref<RaidBoardPayload | null>
  readonly calendarWeek: import('vue').ComputedRef<import('../../types').RaidCalendarWeek>
  readonly numericRaidId: Map<string, number>
  readonly numericRoomId: Map<string, number>
  readonly run: <T>(
    mutation: unknown,
    variables: Record<string, unknown>,
    done?: (result: T) => void,
  ) => Promise<void>
  readonly runReporting: <T>(
    mutation: unknown,
    variables: Record<string, unknown>,
    acknowledge?: (result: T) => boolean,
  ) => Promise<boolean>
  readonly loadStagesForRaid: (raidId: RaidId, shouldApply?: () => boolean) => Promise<boolean>
  readonly fetchRaidBoard: (raidId: RaidId, shouldApply?: () => boolean) => Promise<boolean>
  /**
   * `selectRaid` is owned by `stores/raid/lifecycle.ts` (the snapshot-
   * selection race guard + rollback). `applyRaidToBoard` and `deleteRaid`
   * delegate their post-mutation clearing back through this ctx slot so
   * the same latestSelection counter is in effect — without that, the
   * post-apply clear could race with a concurrent `selectRaid(nextId)`
   * the user queued from the calendar.
   */
  readonly selectRaid: (raidId: RaidId | null) => Promise<boolean>
  /**
   * Host-injected confirm seam. `deleteRaid` and `applyRaidToBoard` gate
   * their destructive mutations through this callback so the store stays
   * confirm-agnostic — `main.ts` wires the port to the browser's
   * confirm dialog, tests can pass a stub, and a future rich UI can
   * swap in a styled modal without touching the store.
   *
   * Captured once at setup-time by the slim composer (see
   * `gvgPlannerStore.ts`); the reference must be stable for the
   * lifetime of the store.
   */
  readonly confirm: (message: string) => boolean
}

export interface RaidActionsApi {
  readonly loadRaids: () => Promise<void>
  readonly createRaid: (input: {
    readonly name: string
    readonly sourceRaidId?: string | null
    readonly scheduledStartsAt?: string | null
    readonly durationMinutes?: number | null
    readonly notes?: string | null
  }) => Promise<void>
  readonly createRaidWithSchedule: (
    name: string,
    scheduledStartsAt: Date | string,
    durationMinutes: number,
    notes: string,
    recurrence?: { readonly frequency: string; readonly count: number } | null,
    sourceRaidId?: RaidId | null,
  ) => Promise<RaidId | null>
  readonly rescheduleRaid: (
    raidId: RaidId,
    scheduledStartsAt: Date | string,
    durationMinutes: number,
    name: string,
    notes: string,
  ) => Promise<void>
  readonly scheduleRaid: (raidId: RaidId, scheduledStartsAt: string) => Promise<void>
  readonly cancelRaid: (raidId: RaidId) => Promise<void>
  readonly setRaidPublished: (raidId: RaidId, isPublished: boolean) => Promise<boolean>
  readonly updateRaid: (
    raidId: RaidId,
    input: {
      readonly name?: string | null
      readonly scheduledStartsAt?: string | null
      readonly durationMinutes?: number | null
      readonly notes?: string | null
    },
  ) => Promise<boolean>
  readonly deleteRaid: (
      raidId: RaidId,
      name: string,
      options?: { readonly skipConfirm?: boolean },
    ) => Promise<boolean>
  readonly applyRaidToBoard: (raidId: RaidId, name: string) => boolean
}

export function useRaidActions(ctx: RaidActionsCtx): RaidActionsApi {
  const loadRaidsForGuild = useLoadRaids(ctx)

  async function loadRaids(): Promise<void> {
    await loadRaidsForGuild()
  }

  async function createRaid(input: {
    readonly name: string
    readonly sourceRaidId?: string | null
    readonly scheduledStartsAt?: string | null
    readonly durationMinutes?: number | null
    readonly notes?: string | null
  }): Promise<void> {
    await ctx.run(RaidCreateDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      name: input.name,
      sourceRaidId: input.sourceRaidId,
      scheduledStartsAt: input.scheduledStartsAt,
      durationMinutes: input.durationMinutes,
      notes: input.notes,
    })
  }

  async function createRaidWithSchedule(
    name: string,
    scheduledStartsAt: Date | string,
    durationMinutes: number,
    notes: string,
    recurrence: { readonly frequency: string; readonly count: number } | null = null,
    sourceRaidId: RaidId | null = null,
  ): Promise<RaidId | null> {
    const scheduledRfc3339 = scheduledStartsAt instanceof Date
      ? scheduledStartsAt.toISOString()
      : scheduledStartsAt
    return requestRaidCreate(apolloClient, ctx.error, {
      guildId: ctx.activeDiscordGuildId.value,
      name,
      sourceRaidId: sourceRaidId === null ? null : Number(sourceRaidId),
      scheduledStartsAt: scheduledRfc3339,
      durationMinutes,
      notes,
      recurrence,
    })
  }

  async function rescheduleRaid(
    raidId: RaidId,
    scheduledStartsAt: Date | string,
    durationMinutes: number,
    name: string,
    notes: string,
  ): Promise<void> {
    const scheduledRfc3339 = scheduledStartsAt instanceof Date
      ? scheduledStartsAt.toISOString()
      : scheduledStartsAt
    await ctx.run(RaidUpdateDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      raidId: Number(raidId),
      name,
      scheduledStartsAt: scheduledRfc3339,
      durationMinutes,
      notes,
    })
  }

  async function updateRaid(
    raidId: RaidId,
    input: {
      readonly name?: string | null
      readonly scheduledStartsAt?: string | null
      readonly durationMinutes?: number | null
      readonly notes?: string | null
    },
  ): Promise<boolean> {
    return ctx.runReporting(RaidUpdateDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      raidId: Number(raidId),
      ...(input.name === undefined ? {} : { name: input.name }),
      ...(input.scheduledStartsAt === undefined ? {} : { scheduledStartsAt: input.scheduledStartsAt }),
      ...(input.durationMinutes === undefined ? {} : { durationMinutes: input.durationMinutes }),
      ...(input.notes === undefined ? {} : { notes: input.notes }),
    })
  }

  async function scheduleRaid(raidId: RaidId, scheduledStartsAt: string): Promise<void> {
    await ctx.run(RaidUpdateDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      raidId: Number(raidId),
      scheduledStartsAt,
    })
  }

  async function cancelRaid(raidId: RaidId): Promise<void> {
    await ctx.run(RaidCancelDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      raidId: Number(raidId),
    })
  }

  async function setRaidPublished(raidId: RaidId, isPublished: boolean): Promise<boolean> {
    return ctx.runReporting(RaidSetPublishedDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      raidId: Number(raidId),
      isPublished,
    })
  }


  function applyRaidToBoard(raidId: RaidId, name: string): boolean {
    if (
      !ctx.confirm(
        `Apply raid "${name}" to the live board? This overwrites every existing room, group, and player.`,
      )
    ) {
      return false
    }
    void ctx.run(RaidApplyToBoardDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      raidId: Number(raidId),
    })
    void ctx.selectRaid(null)
    return true
  }

  /**
   * Deletes a raid. Pass `options.skipConfirm: true` when the caller has
   * already collected user intent (e.g. via `AppModal`) and shouldn't get
   * a second native browser confirm dialog. Callers without their own
   * confirm UI get the port-level `ctx.confirm` fallback as a defensive
   * gate.
   */
  async function deleteRaid(
    raidId: RaidId,
    name: string,
    options?: { readonly skipConfirm?: boolean },
  ): Promise<boolean> {
    if (
      !options?.skipConfirm &&
      !ctx.confirm(`Delete raid "${name}"? This permanently removes the snapshot.`)
    ) {
      return false
    }
    await ctx.run(RaidDeleteDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      raidId: Number(raidId),
    })
    if (ctx.error.value !== null) return false
    if (ctx.activeRaidId.value === raidId) {
      void ctx.selectRaid(null)
    }
    return true
  }

  return {
    loadRaids,
    createRaid,
    createRaidWithSchedule,
    rescheduleRaid,
    scheduleRaid,
    cancelRaid,
    setRaidPublished,
    updateRaid,
    deleteRaid,
    applyRaidToBoard,
  }
}
